// Instances
import { DishBackgroundVideo } from "./background.instance";
import { toast } from "react-toastify";

// Tanslations
import i18n from 'i18next';

// Types
import { ConnectDeviceOptions } from "../dish.types";

/**
 * Detección del entorno de ejecución
 */
const isElectron = (): boolean => {
    return !!(window as any).electronAPI ||
           !!(window as any).require ||
           !!(window as any).process?.type ||
           navigator.userAgent.toLowerCase().includes('electron');
};

/**
 * Configuración específica para Electron con balance ruido/calidad médica
 */
const ELECTRON_AUDIO_CONFIG = {
    // Electron a menudo requiere configuraciones más específicas
    useAdvancedConstraints: true,
    forceHighpassDisable: true,
    useMultipleAttempts: true,
    enableElectronWorkarounds: true,
    // Configuración optimizada para máxima fidelidad médica (inspirado en emurmur)
    enableSmartNoiseReduction: false,   // CAMBIO: Deshabilitado para evitar artefactos
    preserveMedicalFrequencies: true,   // Preservar 0-2000Hz para sonidos médicos completos
    // Configuraciones específicas de Chromium en Electron
    chromiumFlags: [
        '--disable-audio-processing',           // Deshabilitar todo procesamiento automático
        '--disable-audio-input-processing',    // Deshabilitar procesamiento de entrada
        '--disable-audio-output-processing',   // Deshabilitar procesamiento de salida
        '--allow-running-insecure-content',
        '--disable-features=WebRtcApm3',       // Deshabilitar Audio Processing Module v3
        '--disable-features=WebRtcAecm',       // Deshabilitar AECM (Echo Cancellation Mobile)
        '--disable-audio-mixer',               // Deshabilitar mixer de audio
    ]
};

/**
 * Configuración médica optimizada para reducir ruido manteniendo calidad
 */
const MEDICAL_AUDIO_PROFILES = {
    // Perfil ultra-preservativo (anterior - máxima calidad, mucho ruido)
    ULTRA_PRESERVATIVE: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        googHighpassFilter: false,
        googLowpassFilter: false,          // No filtrar altas frecuencias médicas
        googNoiseSuppression: false,
        googAutoGainControl: false,
        description: "Máxima preservación de frecuencias médicas, sin filtrado de ruido"
    },

    // Perfil balanceado (OPTIMIZADO - inspirado en emurmur para alta fidelidad)
    BALANCED_MEDICAL: {
        echoCancellation: false,           // Mantener deshabilitado para no afectar latidos
        noiseSuppression: false,           // CAMBIO: Deshabilitado - el noise suppression puede introducir artefactos
        autoGainControl: false,            // Mantener deshabilitado para preservar dinámicas médicas
        googHighpassFilter: false,         // CRÍTICO: Mantener deshabilitado para respiración (0-100Hz)
        googNoiseSuppression: false,       // CAMBIO: Deshabilitado para evitar artefactos
        googAutoGainControl: false,        // Mantener control manual de ganancia
        googLowpassFilter: false,          // No filtrar altas frecuencias médicas
        googTypingNoiseDetection: false,   // CAMBIO: Deshabilitado para evitar filtrado agresivo
        description: "Perfil de alta fidelidad médica inspirado en emurmur - sin procesamiento de ruido"
    },

    // Perfil emurmur-like (NUEVO - máxima fidelidad sin procesamiento)
    EMURMUR_LIKE: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        googHighpassFilter: false,
        googNoiseSuppression: false,
        googAutoGainControl: false,
        googLowpassFilter: false,
        googTypingNoiseDetection: false,
        googAudioMirroring: false,
        description: "Perfil de máxima fidelidad inspirado en emurmur - cero procesamiento de audio"
    },

    // Perfil experimental para estetoscopios de alta gama
    HIGH_END_STETHOSCOPE: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        googHighpassFilter: false,
        googNoiseSuppression: false,
        googAutoGainControl: false,
        googLowpassFilter: false,
        googTypingNoiseDetection: false,
        googAudioMirroring: false,
        googExperimentalEchoCancellation: false,
        googExperimentalNoiseSuppression: false,
        googExperimentalAutoGainControl: false,
        description: "Perfil para estetoscopios digitales de alta gama - fidelidad máxima"
    },

    // Perfil ultra-limpio (máxima reducción de ruido, puede afectar algunas frecuencias)
    ULTRA_CLEAN: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        googHighpassFilter: false,         // Mantener false para respiración
        googLowpassFilter: false,          // No filtrar altas frecuencias médicas
        googNoiseSuppression: true,
        googAutoGainControl: true,
        googTypingNoiseDetection: true,
        description: "Máxima reducción de ruido, puede afectar algunas frecuencias médicas"
    }
};

/**
 * Perfil activo por defecto - CAMBIO PARA MÁXIMA FIDELIDAD
 */
const ACTIVE_MEDICAL_PROFILE = MEDICAL_AUDIO_PROFILES.EMURMUR_LIKE;

/**
 * Configuración de análisis médico para control en producción
 */
const MEDICAL_DEVICE_DEBUG = {
    enableCapabilityAnalysis: true,     // Análisis de capacidades del dispositivo
    enableConstraintAnalysis: true,     // Análisis de constraints aplicadas
    enableSpectralAnalysis: true,       // Análisis de espectro de frecuencias
    enableAdvancedConstraints: true,    // Análisis de constraints experimentales
    enableDetailedLogging: true,        // Logging detallado paso a paso
    enableElectronVerification: true,   // Verificación específica de configuración Electron
};

/**
 * Función helper para desactivar completamente el análisis médico en producción
 *
 * PARA PRODUCCIÓN:
 * - Cambiar por 'return false'
 *
 * Esto deshabilitará todos los logs y análisis de una vez
 */
const enableMedicalDeviceDebug = (): boolean => {
    return true;
};

/**
 * Device Filter Type
 * @type DeviceFilterType
 */
export type DeviceFilterType = {
    audio?: boolean;
    video?: boolean | {
        pan: true, tilt: true, zoom: true
    };
}

/**
 * Medical Device Options Type
 * Configuración específica para dispositivos médicos como estetoscopios
 */
export type MedicalDeviceOptions = {
    preserveLowFrequencies?: boolean;  // Preservar frecuencias bajas (por defecto true)
    highQualityAudio?: boolean;        // Usar máxima calidad de audio (por defecto true)
    disableProcessing?: boolean;       // Desactivar todo procesamiento de audio (por defecto true)
    sampleRate?: 44100 | 48000;       // Frecuencia de muestreo (por defecto 48000)
}

/**
 * Extended Audio Track Constraints
 * Incluye propiedades experimentales de Chrome para dispositivos médicos de audio
 */
interface ExtendedAudioTrackConstraints extends MediaTrackConstraints {
    googHighpassFilter?: boolean;
    googLowpassFilter?: boolean;
    googEchoCancellation?: boolean;
    googNoiseSuppression?: boolean;
    googAutoGainControl?: boolean;
    googTypingNoiseDetection?: boolean;
    googAudioMirroring?: boolean;
    suppressLocalAudioPlayback?: boolean;
    // Soporte para constraint objects además de valores simples
    sampleRate?: number | { ideal?: number; min?: number; max?: number; exact?: number };
    channelCount?: number | { ideal?: number; min?: number; max?: number; exact?: number };
    latency?: number;
    // Soporte para constraint objects con exact
    echoCancellation?: boolean | { exact?: boolean };
    noiseSuppression?: boolean | { exact?: boolean };
    autoGainControl?: boolean | { exact?: boolean };
    frameRate?: number;
}

/**
 * Extended Video Track Constraints
 * Incluye propiedades experimentales de Chrome para dispositivos de video
 */
interface ExtendedVideoTrackConstraints extends MediaTrackConstraints {
    pan?: boolean;
    tilt?: boolean;
    zoom?: boolean;
}

/**
 * Device Instance
 * @class DeviceInstance
 */
export class DeviceInstance {

    dishBackgroundVideoInstance: DishBackgroundVideo | undefined;

    constructor() {
      console.log(`[DeviceInstance] constructor`)
    }

    async stopBackground() {
        try {
            if (this.dishBackgroundVideoInstance) {
                console.log("Stopping background video instance");
                this.dishBackgroundVideoInstance.stop();
                this.dishBackgroundVideoInstance = undefined;
            }
        } catch (error) {
            console.error("Error stopping background:", error);
        }
    }

    async replaceBackground(stream: MediaStream, image: string) {
        // First, make sure any existing background instance is stopped
        await this.stopBackground();

        return new Promise<MediaStream>((resolve, reject) => {
            // create image html
            const background = document.createElement('img');
            let backgroundAdded = false;

            const cleanupBackground = () => {
                if (background && backgroundAdded && background.parentNode) {
                    background.parentNode.removeChild(background);
                    backgroundAdded = false;
                }
            };

            background.src = image;
            // set in body
            document.body.appendChild(background);
            backgroundAdded = true;

            // position fixed zindex:1000
            background.style.position = 'fixed';
            background.style.top = '0';
            background.style.left = '0';
            background.style.zIndex = '-1000';
            // pointer events none
            background.style.pointerEvents = 'none';
            // hide
            background.style.opacity = '0';

            // Handle image load errors
            background.onerror = () => {
                cleanupBackground();
                console.error('Failed to load background image');
                // If image fails, return original stream instead of rejecting
                resolve(stream);
            };

            background.onload = async () => {
                // Let's ensure the image is fully loaded with a reasonable size
                if (background.width === 0 || background.height === 0) {
                    cleanupBackground();
                    console.error('Background image has invalid dimensions');
                    resolve(stream);
                    return;
                }

                try {
                    // Wait for model to initialize
                    setTimeout(async () => {
                        try {
                            const segmentation = new DishBackgroundVideo({ stream, background });
                            this.dishBackgroundVideoInstance = segmentation;
                            segmentation.run();

                            // Add a timeout to handle cases where getStream might hang
                            const timeoutPromise = new Promise<MediaStream>((_, reject) => {
                                setTimeout(() => reject(new Error('Background processing timeout')), 5000);
                            });

                            try {
                                const streamResult = await Promise.race([
                                    segmentation.getStream(),
                                    timeoutPromise
                                ]);

                                // Set up proper cleanup for the stream
                                const cleanupHandler = () => {
                                    console.log("Stream ended or inactive, cleaning up background");
                                    if (this.dishBackgroundVideoInstance === segmentation) {
                                        this.stopBackground();
                                    }
                                };

                                // Clean up on stream end
                                stream.addEventListener('inactive', cleanupHandler);
                                stream.addEventListener('ended', cleanupHandler);

                                // Add cleanup handler to result stream too
                                streamResult.addEventListener('inactive', cleanupHandler);
                                streamResult.addEventListener('ended', cleanupHandler);

                                resolve(streamResult);
                            } catch (error) {
                                console.error('Failed to get processed stream:', error);
                                if (this.dishBackgroundVideoInstance === segmentation) {
                                    this.stopBackground();
                                }
                                cleanupBackground();
                                // Fallback to original stream
                                resolve(stream);
                            }
                        } catch (initError) {
                            console.error('Error initializing background processing:', initError);
                            cleanupBackground();
                            resolve(stream);
                        }
                    }, 500);
                } catch (error) {
                    console.error('Error in background processing:', error);
                    cleanupBackground();
                    resolve(stream);
                }
            };
        });
    }

    /**
     * Verifica si las configuraciones de Electron están siendo efectivas
     * Función específica para diagnosticar problemas de configuración Electron/Chromium
     */
    private async verifyElectronAudioConfiguration(): Promise<void> {
        const runningInElectron = isElectron();

        if (!runningInElectron) {
            console.log('🌐 Running in browser - Electron verification skipped');
            return;
        }

        console.log('🖥️ ELECTRON CONFIGURATION VERIFICATION');
        console.log('=====================================');

        // Verificar si las APIs de Electron están disponibles
        const electronAPIs = {
            electronAPI: !!(window as any).electronAPI,
            require: !!(window as any).require,
            processType: !!(window as any).process?.type,
            userAgent: navigator.userAgent.toLowerCase().includes('electron'),
        };

        console.log('🔍 Electron Environment Detection:', electronAPIs);

        // Verificar configuraciones esperadas
        const expectedConfigs = {
            userAgent: navigator.userAgent,
            chromiumVersion: navigator.userAgent.match(/Chrome\/(\d+)/)?.[1] || 'unknown',
            electronVersion: navigator.userAgent.match(/Electron\/(\d+\.\d+\.\d+)/)?.[1] || 'unknown',
        };

        console.log('📋 Environment Details:', expectedConfigs);

        // Verificar si los command line switches están activos (indirectamente)
        try {
            // Intentar crear un AudioContext para verificar configuraciones
            const testAudioContext = new AudioContext();
            const testOscillator = testAudioContext.createOscillator();
            const testAnalyser = testAudioContext.createAnalyser();

            testOscillator.connect(testAnalyser);
            testOscillator.start();

            console.log('🎵 Audio Context Test:', {
                sampleRate: testAudioContext.sampleRate,
                state: testAudioContext.state,
                baseLatency: testAudioContext.baseLatency,
                outputLatency: testAudioContext.outputLatency,
            });

            testOscillator.stop();
            testAudioContext.close();
        } catch (audioTestError) {
            console.warn('⚠️ Audio Context test failed:', audioTestError);
        }

        // Verificar disponibilidad de MediaDevices API
        if (navigator.mediaDevices) {
            console.log('✅ MediaDevices API available');

            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const audioDevices = devices.filter(device => device.kind === 'audioinput');
                console.log(`🎙️ Audio input devices found: ${audioDevices.length}`);

                // Mostrar dispositivos para debugging
                audioDevices.forEach((device, index) => {
                    console.log(`   ${index + 1}. ${device.label || 'Unnamed device'} (${device.deviceId})`);
                });

            } catch (deviceError) {
                console.error('❌ Error enumerating devices:', deviceError);
            }
        } else {
            console.error('❌ MediaDevices API not available');
        }

        // Verificar permisos
        try {
            const permissions = await navigator.permissions.query({ name: 'microphone' as PermissionName });
            console.log('🔐 Microphone permission:', permissions.state);
        } catch (permissionError) {
            console.warn('⚠️ Permission check failed:', permissionError);
        }

        // Mensaje de status final
        console.log('=====================================');
        if (electronAPIs.electronAPI || electronAPIs.require || electronAPIs.processType) {
            console.log('✅ ELECTRON DETECTED - Audio optimizations should be active');
            console.log('🔧 If respiration sounds are still missing, check:');
            console.log('   1. Main process command line switches are applied');
            console.log('   2. BrowserWindow webPreferences configuration');
            console.log('   3. Electron version compatibility');
        } else {
            console.warn('❓ ELECTRON DETECTION UNCERTAIN');
            console.warn('   - May be running in embedded browser mode');
            console.warn('   - Command line switches may not be effective');
        }
        console.log('=====================================');
    }

    /**
     * Analiza las capacidades de un dispositivo médico específico
     * Útil para debugging y optimización de configuraciones
     */
    async analyzeMedicalDeviceCapabilities(deviceId: string): Promise<any> {
        try {
            console.log('🔍 Analyzing medical device capabilities...');

            // Obtener un stream temporal para acceder a las capacidades
            const tempStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    deviceId: { exact: deviceId }
                }
            });

            const audioTrack = tempStream.getAudioTracks()[0];
            if (audioTrack) {
                const capabilities = audioTrack.getCapabilities ? audioTrack.getCapabilities() : {};
                const settings = audioTrack.getSettings();

                console.log('📊 Device Capabilities Analysis:', {
                    label: audioTrack.label,
                    deviceId: settings.deviceId,
                    capabilities: capabilities,
                    recommendations: {
                        sampleRate: capabilities.sampleRate ?
                            `Use ${Math.max(...(Array.isArray(capabilities.sampleRate) ? [44100, 48000] : [capabilities.sampleRate.max || 48000]))}Hz for best quality` :
                            'Sample rate constraints not available',
                        channelCount: capabilities.channelCount ?
                            `Device supports ${capabilities.channelCount.min}-${capabilities.channelCount.max} channels. Use 1 for mono (recommended for stethoscope)` :
                            'Channel count constraints not available',
                        latency: (capabilities as any).latency ?
                            `Optimal latency range: ${(capabilities as any).latency.min}-${(capabilities as any).latency.max}s` :
                            'Latency constraints not available',
                        processing: 'Disable all audio processing (echo cancellation, noise suppression, auto gain control) for medical devices'
                    }
                });

                // Limpiar el stream temporal
                tempStream.getTracks().forEach(track => track.stop());

                return capabilities;
            }
        } catch (error) {
            console.error('Error analyzing device capabilities:', error);
            return null;
        }
    }

    /**
     * Realiza análisis completo del dispositivo médico y logging detallado
     * Función externalizada para fácil control en producción
     */
    private async performMedicalDeviceAnalysis(
        audioTrack: MediaStreamTrack,
        settings: MediaTrackSettings,
        capabilities: MediaTrackCapabilities,
        options: {
            sampleRate: number;
            requestedChannelCount?: number;
            enableSpectralAnalysis?: boolean;
        }
    ): Promise<void> {
        const { sampleRate, requestedChannelCount = 1, enableSpectralAnalysis = true } = options;

        // === ANÁLISIS DE CONFIGURACIÓN INICIAL ===
        console.log('🔧 Initial medical device stream configured:', {
            deviceId: settings.deviceId,
            sampleRate: settings.sampleRate,
            echoCancellation: settings.echoCancellation,
            noiseSuppression: settings.noiseSuppression,
            autoGainControl: settings.autoGainControl,
            label: audioTrack.label,
            channelCount: (settings as any).channelCount || 'unknown',
            latency: (settings as any).latency || 'unknown',
            voiceIsolation: (settings as any).voiceIsolation || 'unknown'
        });

        // === ANÁLISIS DE RESULTADOS FINALES ===
        console.log('📊 Final medical device configuration:', {
            deviceId: settings.deviceId,
            sampleRate: settings.sampleRate,
            echoCancellation: settings.echoCancellation,
            noiseSuppression: settings.noiseSuppression,
            autoGainControl: settings.autoGainControl,
            label: audioTrack.label,
            channelCount: (settings as any).channelCount || 'unknown',
            latency: (settings as any).latency || 'unknown',
            voiceIsolation: (settings as any).voiceIsolation || 'unknown'
        });

        // === ANÁLISIS DE CUMPLIMIENTO DE CONSTRAINTS ===
        const warnings = [];
        const successes = [];

        if (settings.sampleRate !== sampleRate) {
            warnings.push(`Sample rate: requested ${sampleRate}Hz, got ${settings.sampleRate}Hz`);
        } else {
            successes.push(`Sample rate: ${settings.sampleRate}Hz ✓`);
        }

        if ((settings as any).channelCount && (settings as any).channelCount !== requestedChannelCount) {
            warnings.push(`Channel count: requested ${requestedChannelCount}, got ${(settings as any).channelCount} channels`);
        } else if ((settings as any).channelCount === requestedChannelCount) {
            successes.push(`Channel count: ${requestedChannelCount} ✓`);
        }

        if (settings.echoCancellation !== false) {
            warnings.push(`Echo cancellation: requested false, got ${settings.echoCancellation}`);
        } else {
            successes.push(`Echo cancellation: disabled ✓`);
        }

        if (settings.noiseSuppression !== false) {
            warnings.push(`Noise suppression: requested false, got ${settings.noiseSuppression}`);
        } else {
            successes.push(`Noise suppression: disabled ✓`);
        }

        if (settings.autoGainControl !== false) {
            warnings.push(`Auto gain control: requested false, got ${settings.autoGainControl}`);
        } else {
            successes.push(`Auto gain control: disabled ✓`);
        }

        if (successes.length > 0) {
            console.log('Medical device optimal settings applied:', successes);
        }

        if (warnings.length > 0) {
            console.info('📝 Medical device hardware specifications:', warnings);
            console.log('ℹ️ Device capabilities:', capabilities);
            console.log('✅ This is expected behavior - the device hardware operates within these specifications');
            console.log('🩺 Device is optimally configured for medical use with all audio processing disabled');
        } else {
            console.log('✅ All medical device constraints successfully applied!');
        }

        // === ANÁLISIS ESPECTRAL (si está habilitado) ===
        if (enableSpectralAnalysis) {
            console.log('🔬 Performing audio spectrum analysis to verify filter effects...');
            await this.analyzeAudioStreamFilters(audioTrack);
        }
    }

    /**
     * Realiza análisis avanzado de constraints experimentales de Google
     * Función externalizada para fácil control en producción
     */
    private async performAdvancedConstraintsAnalysis(
        audioTrack: MediaStreamTrack,
        settings: MediaTrackSettings
    ): Promise<void> {
        console.log('🔍 Advanced constraints application results:');
        console.log('Standard settings after advanced constraints:', {
            echoCancellation: settings.echoCancellation,
            noiseSuppression: settings.noiseSuppression,
            autoGainControl: settings.autoGainControl
        });

        // Mostrar capacidades actualizadas si están disponibles
        const updatedCapabilities = audioTrack.getCapabilities ? audioTrack.getCapabilities() : {};
        if (Object.keys(updatedCapabilities).length > 0) {
            console.log('Updated device capabilities:', updatedCapabilities);
        }

        // Intentar obtener información sobre las constraints aplicadas
        console.log('🔧 Applied constraints verification:');
        try {
            // Verificar si las constraints fueron aceptadas comparando con el estado anterior
            const constraintsApplied = {
                googHighpassFilter: '❓ Cannot verify (experimental constraint)',
                googLowpassFilter: '❓ Cannot verify (experimental constraint)',
                googEchoCancellation: settings.echoCancellation === false ? '✅ Disabled' : '❌ Still enabled',
                googNoiseSuppression: settings.noiseSuppression === false ? '✅ Disabled' : '❌ Still enabled',
                googAutoGainControl: settings.autoGainControl === false ? '✅ Disabled' : '❌ Still enabled',
            };

            console.log('Advanced constraints status:', constraintsApplied);

            // Información adicional sobre por qué no podemos verificar googXXX
            console.log('ℹ️ Note: Google experimental constraints (googXXX) are not exposed in getSettings()');
            console.log('ℹ️ Their effects are applied internally by Chrome but not directly observable');
            console.log('ℹ️ Standard constraints (echoCancellation, etc.) are the observable equivalents');

        } catch (verificationError) {
            console.warn('Could not verify advanced constraints:', verificationError);
        }

        // Realizar análisis de espectro de audio para verificación indirecta
        console.log('🔬 Performing audio spectrum analysis to verify filter effects...');
        await this.analyzeAudioStreamFilters(audioTrack);
    }

    /**
     * Analiza el stream de audio para detectar indirectamente si los filtros están activo
     * Útil para verificar si las constraints experimentales de Google se aplicaron
     */
    async analyzeAudioStreamFilters(audioTrack: MediaStreamTrack): Promise<void> {
        try {
            const runningInElectron = isElectron();
            console.log(`🎵 Analyzing audio stream for filter detection (${runningInElectron ? 'Electron' : 'Browser'})...`);

            // Crear un AudioContext para analizar el stream
            const audioContext = new AudioContext();
            const source = audioContext.createMediaStreamSource(new MediaStream([audioTrack]));
            const analyser = audioContext.createAnalyser();

            analyser.fftSize = 2048;
            analyser.smoothingTimeConstant = 0.8;
            source.connect(analyser);

            const bufferLength = analyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);

            // Tomar varias muestras para analizar el espectro
            setTimeout(() => {
                analyser.getByteFrequencyData(dataArray);

                // Analizar diferentes bandas de frecuencia médicamente relevantes
                // Calcular frecuencia por bin: sampleRate / 2 / bufferLength
                const sampleRate = audioContext.sampleRate;
                const nyquist = sampleRate / 2;
                const binSize = nyquist / bufferLength;

                // Bandas específicas para diagnóstico médico
                const veryLowFreqEnd = Math.floor(100 / binSize);      // 0-100Hz - Sonidos respiratorios
                const lowFreqEnd = Math.floor(430 / binSize);          // 100-430Hz - Sonidos cardíacos
                const midFreqEnd = Math.floor(2150 / binSize);         // 430-2150Hz - Rango general de audio
                const highFreqEnd = Math.floor(4300 / binSize);        // 2150-4300Hz - Rango superior

                const veryLowFreqSum = dataArray.slice(0, veryLowFreqEnd).reduce((a, b) => a + b, 0) / veryLowFreqEnd; // 0-100Hz
                const lowFreqSum = dataArray.slice(veryLowFreqEnd, lowFreqEnd).reduce((a, b) => a + b, 0) / (lowFreqEnd - veryLowFreqEnd); // 100-430Hz
                const midFreqSum = dataArray.slice(lowFreqEnd, midFreqEnd).reduce((a, b) => a + b, 0) / (midFreqEnd - lowFreqEnd); // 430-2150Hz
                const highFreqSum = dataArray.slice(midFreqEnd, highFreqEnd).reduce((a, b) => a + b, 0) / (highFreqEnd - midFreqEnd); // 2150-4300Hz

                console.dir('🎛️ Medical audio frequency analysis:', {
                    sampleRate: `${sampleRate}Hz`,
                    binSize: `${binSize.toFixed(2)}Hz per bin`,
                    respirationBand: `${veryLowFreqSum.toFixed(1)} (0-100Hz) - 🫁 Sonidos respiratorios`,
                    heartSoundsBand: `${lowFreqSum.toFixed(1)} (100-430Hz) - 💓 Sonidos cardíacos`,
                    generalAudioBand: `${midFreqSum.toFixed(1)} (430-2150Hz) - 🔊 Rango general de audio`,
                    upperFreqBand: `${highFreqSum.toFixed(1)} (2150-4300Hz) - 📈 Frecuencias superiores`
                });

                // Análisis específico para el problema del cliente con respiración/espiración
                if (veryLowFreqSum < 5) {
                    console.error('🚨 PROBLEMA:');
                    console.error('   - La banda 0-100Hz está muy atenuada');
                    console.error('   - El cliente NO PUEDE escuchar la respiración/espiración');
                    console.error('   - Posible causa: Filtro highpass activo');
                    console.error('   - Solución: Verificar que googHighpassFilter = false');
                } else if (veryLowFreqSum < 15) {
                    console.warn('⚠️ ADVERTENCIA: Respiración parcialmente atenuada');
                    console.warn('   - Banda 0-100Hz con nivel bajo pero audible');
                    console.warn('   - Cliente puede tener dificultades para escuchar respiración débil');
                } else {
                    console.log('✅ EXCELENTE: Banda de respiración (0-100Hz) preservada correctamente');
                    console.log('   - Cliente debe poder escuchar la respiración/espiración claramente');
                }                // Limpiar recursos
                source.disconnect();
                audioContext.close();
            }, 1000); // Esperar 1 segundo para obtener datos

        } catch (error) {
            console.warn('Could not analyze audio stream:', error);
        }
    }

    /**
     * Obtiene un stream de dispositivo médico con análisis completo de capacidades
     * Útil para debugging y desarrollo
     */
    async getMedicalDeviceStreamWithAnalysis(
        deviceId: string,
        options: MedicalDeviceOptions = {}
    ): Promise<{ stream: MediaStream | undefined, capabilities: any }> {
        console.log('🔬 Starting comprehensive medical device analysis...');

        const capabilities = await this.analyzeMedicalDeviceCapabilities(deviceId);
        const stream = await this.getMedicalDeviceStream(deviceId, options);

        return { stream, capabilities };
    }

    /**
     * Obtiene un stream optimizado para dispositivos médicos como estetoscopios
     * Configuración sin filtros para preservar todas las frecuencias
     *
     * ANÁLISIS Y DEBUG:
     * - En desarrollo: Se ejecuta análisis completo automáticamente
     * - En producción: Análisis deshabilitado por defecto
     * - Control: Cambiar enableMedicalDeviceDebug() para controlar logs
     *
     * @param deviceId - ID del dispositivo médico
     * @param options - Opciones de configuración médica
     * @returns Stream optimizado para uso médico
     */
    async getMedicalDeviceStream(
        deviceId: string,
        options: MedicalDeviceOptions = {}
    ): Promise<MediaStream | undefined> {
        const {
            preserveLowFrequencies = true,
            highQualityAudio = true,
            sampleRate = 48000
        } = options;

        try {
            // Detectar si estamos en Electron
            const runningInElectron = isElectron();
            if (runningInElectron) {
                console.log('🖥️ Electron environment detected - using specialized medical device configuration...');
                console.log('🔧 Electron audio config:', ELECTRON_AUDIO_CONFIG);

                // Verificación específica de configuración Electron
                if (enableMedicalDeviceDebug() && MEDICAL_DEVICE_DEBUG.enableElectronVerification) {
                    await this.verifyElectronAudioConfiguration();
                }
            }

            // Analizar capacidades del dispositivo primero (para debugging)
            if (enableMedicalDeviceDebug() && MEDICAL_DEVICE_DEBUG.enableCapabilityAnalysis) {
                console.log('🔬 Analyzing device capabilities before configuration...');
                await this.analyzeMedicalDeviceCapabilities(deviceId);
            }

            // Configuración optimizada para dispositivos médicos basada en capacidades reales
            // Configuración específica para Electron vs navegador web
            let audioConstraints: ExtendedAudioTrackConstraints;

            if (runningInElectron && ELECTRON_AUDIO_CONFIG.enableElectronWorkarounds) {
                console.log('🖥️ Using Electron-specific medical device constraints with EMURMUR_LIKE profile...');
                console.log('🎯 Active profile:', ACTIVE_MEDICAL_PROFILE.description);

                audioConstraints = {
                    deviceId: deviceId ? { exact: deviceId } : undefined,
                    // CONFIGURACIÓN EMURMUR_LIKE: Máxima fidelidad sin procesamiento
                    echoCancellation: ACTIVE_MEDICAL_PROFILE.echoCancellation,
                    noiseSuppression: ACTIVE_MEDICAL_PROFILE.noiseSuppression,
                    autoGainControl: ACTIVE_MEDICAL_PROFILE.autoGainControl,
                    // Configuraciones de calidad premium para audio médico
                    sampleRate: sampleRate,
                    channelCount: 1,                           // Mono para estetoscopios
                    latency: 0.001,                           // Ultra-baja latencia para tiempo real
                    // Configuraciones experimentales de Chrome/Electron DESHABILITADAS para máxima fidelidad
                    googEchoCancellation: ACTIVE_MEDICAL_PROFILE.echoCancellation,
                    googNoiseSuppression: ACTIVE_MEDICAL_PROFILE.googNoiseSuppression,
                    googAutoGainControl: ACTIVE_MEDICAL_PROFILE.googAutoGainControl,
                    googHighpassFilter: ACTIVE_MEDICAL_PROFILE.googHighpassFilter,        // CRÍTICO: false para respiración
                    googLowpassFilter: ACTIVE_MEDICAL_PROFILE.googLowpassFilter || false, // CRÍTICO: false para murmures agudos
                    googTypingNoiseDetection: ACTIVE_MEDICAL_PROFILE.googTypingNoiseDetection || false,
                    googAudioMirroring: false,
                    // Configuraciones experimentales adicionales para Electron DESHABILITADAS
                    googExperimentalEchoCancellation: ACTIVE_MEDICAL_PROFILE.echoCancellation || false,
                    googExperimentalNoiseSuppression: ACTIVE_MEDICAL_PROFILE.googNoiseSuppression || false,
                    googExperimentalAutoGainControl: ACTIVE_MEDICAL_PROFILE.googAutoGainControl || false,
                    // Configuraciones específicas de procesamiento de audio DESHABILITADAS para fidelidad
                    suppressLocalAudioPlayback: false,
                    audioProcessing: false,                    // CRÍTICO: Sin procesamiento automático
                    voiceIsolation: false,                     // CRÍTICO: Sin aislamiento de voz
                    // Configuraciones adicionales específicas para audio médico
                    disableLocalEcho: true,                    // Evitar eco local
                    disableHighpassFilter: true,               // CRÍTICO: Preservar frecuencias bajas completamente
                    disableAudioProcessing: true,              // CRÍTICO: Sin procesamiento de DSP
                } as any;
            } else {
                // Configuración estándar para navegadores web con perfil EMURMUR_LIKE
                console.log('🌐 Using standard browser constraints with EMURMUR_LIKE profile...');
                console.log('🎯 Active profile:', ACTIVE_MEDICAL_PROFILE.description);

                audioConstraints = {
                    deviceId: deviceId ? { exact: deviceId } : undefined,
                    // CONFIGURACIÓN EMURMUR_LIKE: Máxima fidelidad sin procesamiento
                    echoCancellation: { exact: ACTIVE_MEDICAL_PROFILE.echoCancellation },
                    noiseSuppression: { exact: ACTIVE_MEDICAL_PROFILE.noiseSuppression },
                    autoGainControl: { exact: ACTIVE_MEDICAL_PROFILE.autoGainControl },
                    // Configuraciones de calidad premium para audio médico
                    sampleRate: { exact: sampleRate },
                    channelCount: { exact: 1 },
                    latency: highQualityAudio ? 0.001 : undefined,  // Ultra-baja latencia
                    // Configuraciones experimentales DESHABILITADAS para máxima fidelidad
                    googEchoCancellation: ACTIVE_MEDICAL_PROFILE.echoCancellation,
                    googNoiseSuppression: ACTIVE_MEDICAL_PROFILE.googNoiseSuppression,
                    googAutoGainControl: ACTIVE_MEDICAL_PROFILE.googAutoGainControl,
                    googHighpassFilter: ACTIVE_MEDICAL_PROFILE.googHighpassFilter,        // CRÍTICO: false
                    googLowpassFilter: ACTIVE_MEDICAL_PROFILE.googLowpassFilter || false, // CRÍTICO: false
                    googTypingNoiseDetection: ACTIVE_MEDICAL_PROFILE.googTypingNoiseDetection || false,
                    googAudioMirroring: false,
                    // Configuraciones experimentales adicionales DESHABILITADAS
                    googExperimentalEchoCancellation: ACTIVE_MEDICAL_PROFILE.echoCancellation || false,
                    googExperimentalNoiseSuppression: ACTIVE_MEDICAL_PROFILE.googNoiseSuppression || false,
                    googExperimentalAutoGainControl: ACTIVE_MEDICAL_PROFILE.googAutoGainControl || false,
                } as any;
            }

            console.log('🎯 First attempt: Requesting medical device stream with EXACT constraints:', audioConstraints);

            let localStream: MediaStream | undefined = undefined;

            // Estrategia 1: Intentar con constraints exactas
            try {
                localStream = await navigator.mediaDevices.getUserMedia({
                    audio: audioConstraints,
                    video: false
                });
                console.log('✅ Success with exact constraints!');
            } catch (exactError) {
                console.warn('❌ Exact constraints failed:', exactError);

                // Estrategia 2: Fallback con ideal/min/max - adaptado para Electron
                console.log(`🔄 Trying fallback strategy with ideal constraints (${runningInElectron ? 'Electron' : 'Browser'})...`);

                if (runningInElectron) {
                    // Fallback específico para Electron con perfil balanceado menos restrictivo
                    console.log('🔄 Electron fallback: Using balanced profile without exact constraints...');
                    audioConstraints = {
                        deviceId: deviceId ? { exact: deviceId } : undefined,
                        // Usar perfil balanceado pero sin exact constraints
                        echoCancellation: ACTIVE_MEDICAL_PROFILE.echoCancellation,
                        noiseSuppression: ACTIVE_MEDICAL_PROFILE.noiseSuppression,
                        autoGainControl: ACTIVE_MEDICAL_PROFILE.autoGainControl,
                        sampleRate: sampleRate,
                        channelCount: 1,
                        latency: 0.01,
                        // Configuraciones experimentales con perfil balanceado
                        googEchoCancellation: ACTIVE_MEDICAL_PROFILE.echoCancellation,
                        googNoiseSuppression: ACTIVE_MEDICAL_PROFILE.googNoiseSuppression,
                        googAutoGainControl: ACTIVE_MEDICAL_PROFILE.googAutoGainControl,
                        googHighpassFilter: ACTIVE_MEDICAL_PROFILE.googHighpassFilter,   // CRÍTICO: false para respiración
                        googLowpassFilter: ACTIVE_MEDICAL_PROFILE.googLowpassFilter || false,
                        googTypingNoiseDetection: ACTIVE_MEDICAL_PROFILE.googTypingNoiseDetection || false,
                        googAudioMirroring: false,
                        // Configuraciones experimentales adicionales para Electron
                        googExperimentalEchoCancellation: ACTIVE_MEDICAL_PROFILE.echoCancellation,
                        googExperimentalNoiseSuppression: ACTIVE_MEDICAL_PROFILE.googNoiseSuppression,
                        googExperimentalAutoGainControl: ACTIVE_MEDICAL_PROFILE.googAutoGainControl,
                        // Configuraciones específicas de Electron/Chromium
                        suppressLocalAudioPlayback: false,
                        audioProcessing: false,
                        voiceIsolation: false,
                        // Configuraciones adicionales que pueden funcionar en Electron
                        disableLocalEcho: true,
                        disableHighpassFilter: true,
                    } as any;
                } else {
                    // Fallback estándar para navegadores web con perfil balanceado
                    console.log('🔄 Browser fallback: Using balanced profile with flexible constraints...');
                    audioConstraints = {
                        deviceId: deviceId ? { exact: deviceId } : undefined,
                        // Usar perfil balanceado pero con ideal en lugar de exact
                        echoCancellation: { ideal: ACTIVE_MEDICAL_PROFILE.echoCancellation },
                        noiseSuppression: { ideal: ACTIVE_MEDICAL_PROFILE.noiseSuppression },
                        autoGainControl: { ideal: ACTIVE_MEDICAL_PROFILE.autoGainControl },
                        sampleRate: {
                            ideal: sampleRate,
                            min: 44100,
                            max: 48000
                        },
                        channelCount: { ideal: 1, min: 1, max: 2 },
                        latency: highQualityAudio ? 0.01 : undefined,
                        // Configuraciones experimentales de Chrome con perfil balanceado
                        googEchoCancellation: ACTIVE_MEDICAL_PROFILE.echoCancellation,
                        googNoiseSuppression: ACTIVE_MEDICAL_PROFILE.googNoiseSuppression,
                        googAutoGainControl: ACTIVE_MEDICAL_PROFILE.googAutoGainControl,
                        googHighpassFilter: ACTIVE_MEDICAL_PROFILE.googHighpassFilter,  // CRÍTICO: false para respiración
                        googLowpassFilter: ACTIVE_MEDICAL_PROFILE.googLowpassFilter || false,
                        googTypingNoiseDetection: ACTIVE_MEDICAL_PROFILE.googTypingNoiseDetection || false,
                        googAudioMirroring: false,
                        // Configuraciones experimentales adicionales
                        googExperimentalEchoCancellation: ACTIVE_MEDICAL_PROFILE.echoCancellation,
                        googExperimentalNoiseSuppression: ACTIVE_MEDICAL_PROFILE.googNoiseSuppression,
                        googExperimentalAutoGainControl: ACTIVE_MEDICAL_PROFILE.googAutoGainControl,
                    } as any;
                }

                try {
                    localStream = await navigator.mediaDevices.getUserMedia({
                        audio: audioConstraints,
                        video: false
                    });
                    console.log('✅ Success with fallback constraints!');
                } catch (fallbackError) {
                    console.error('❌ Both strategies failed:', fallbackError);
                    throw fallbackError;
                }
            }

            // Análisis del stream configurado (si está habilitado)
            const audioTrack = localStream!.getAudioTracks()[0];
            if (audioTrack && enableMedicalDeviceDebug()) {
                const settings = audioTrack.getSettings();
                const capabilities = audioTrack.getCapabilities ? audioTrack.getCapabilities() : {};

                // Realizar análisis completo del dispositivo médico
                if (MEDICAL_DEVICE_DEBUG.enableConstraintAnalysis) {
                    await this.performMedicalDeviceAnalysis(audioTrack, settings, capabilities, {
                        sampleRate,
                        requestedChannelCount: 1,
                        enableSpectralAnalysis: MEDICAL_DEVICE_DEBUG.enableSpectralAnalysis
                    });
                }

                // Estrategia 3: Usar applyConstraints para intentar ajustar después
                if (enableMedicalDeviceDebug() && MEDICAL_DEVICE_DEBUG.enableDetailedLogging &&
                    (settings.sampleRate !== sampleRate || (settings as any).channelCount !== 1)) {
                    console.log('🔄 Attempting post-configuration optimization with applyConstraints...');
                    try {
                        await audioTrack.applyConstraints({
                            sampleRate: { exact: sampleRate },
                            channelCount: { exact: 1 },
                            echoCancellation: { exact: false },
                            noiseSuppression: { exact: false },
                            autoGainControl: { exact: false },
                        } as any);

                        const updatedSettings = audioTrack.getSettings();
                        console.log('🎯 Post-configuration results:', {
                            sampleRate: `${settings.sampleRate} → ${updatedSettings.sampleRate}`,
                            channelCount: `${(settings as any).channelCount} → ${(updatedSettings as any).channelCount}`,
                            echoCancellation: updatedSettings.echoCancellation,
                            noiseSuppression: updatedSettings.noiseSuppression,
                            autoGainControl: updatedSettings.autoGainControl
                        });
                    } catch (applyError) {
                        console.info('ℹ️ Post-configuration optimization not supported by device hardware:', (applyError as Error).message);
                        console.log('✅ This is normal - the device already provides optimal settings for medical use');
                    }
                }

                // Aplicar configuraciones avanzadas si están disponibles
                if (preserveLowFrequencies && audioTrack.applyConstraints &&
                    enableMedicalDeviceDebug() && MEDICAL_DEVICE_DEBUG.enableAdvancedConstraints) {
                    console.log(`🔧 Applying AGGRESSIVE advanced constraints for ${runningInElectron ? 'ELECTRON' : 'BROWSER'} to preserve low frequencies...`);

                    // Estrategia múltiple: Configuraciones específicas para Electron vs Browser
                    let advancedAttempts;

                    if (runningInElectron) {
                        // Configuraciones específicas para Electron - más directo, menos advanced arrays
                        advancedAttempts = [
                            // Intento 1: Configuración directa para Electron
                            {
                                googHighpassFilter: false,
                                googLowpassFilter: false,
                                googEchoCancellation: false,
                                googNoiseSuppression: false,
                                googAutoGainControl: false,
                                audioProcessing: false,
                                suppressLocalAudioPlayback: false,
                            } as any,
                            // Intento 2: Solo el filtro highpass (más específico para Electron)
                            {
                                googHighpassFilter: false,
                                disableHighpassFilter: true,
                            } as any,
                            // Intento 3: Configuración Electron experimental
                            {
                                advanced: [{
                                    googHighpassFilter: false,
                                    googLowpassFilter: false,
                                    audioProcessing: false,
                                    suppressLocalAudioPlayback: false,
                                    disableLocalEcho: true,
                                } as any]
                            },
                            // Intento 4: Configuración extendida para Electron
                            {
                                advanced: [{
                                    googHighpassFilter: false,
                                    googLowpassFilter: false,
                                    googEchoCancellation: false,
                                    googNoiseSuppression: false,
                                    googAutoGainControl: false,
                                    googTypingNoiseDetection: false,
                                    googAudioMirroring: false,
                                    googExperimentalEchoCancellation: false,
                                    googExperimentalNoiseSuppression: false,
                                    googExperimentalAutoGainControl: false,
                                    audioProcessing: false,
                                    voiceIsolation: false,
                                } as any]
                            }
                        ];
                    } else {
                        // Configuraciones estándar para navegadores web
                        advancedAttempts = [
                            // Intento 1: Configuración básica
                            {
                                advanced: [{
                                    googHighpassFilter: false,
                                    googLowpassFilter: false,
                                    googEchoCancellation: false,
                                    googNoiseSuppression: false,
                                    googAutoGainControl: false,
                                } as any]
                            },
                            // Intento 2: Configuración extendida
                            {
                                advanced: [{
                                    googHighpassFilter: false,
                                    googLowpassFilter: false,
                                    googEchoCancellation: false,
                                    googNoiseSuppression: false,
                                    googAutoGainControl: false,
                                    googTypingNoiseDetection: false,
                                    googAudioMirroring: false,
                                    googExperimentalEchoCancellation: false,
                                    googExperimentalNoiseSuppression: false,
                                    googExperimentalAutoGainControl: false,
                                } as any]
                            },
                            // Intento 3: Solo googHighpassFilter (más específico)
                            {
                                advanced: [{
                                    googHighpassFilter: false,
                                } as any]
                            }
                        ];
                    }

                    let constraintsSuccess = false;
                    for (let i = 0; i < advancedAttempts.length; i++) {
                        try {
                            console.log(`🎯 Advanced constraints attempt ${i + 1}/3...`);
                            await audioTrack.applyConstraints(advancedAttempts[i]);
                            console.log(`✅ Advanced constraints attempt ${i + 1} successful!`);
                            constraintsSuccess = true;
                            break;
                        } catch (constraintError) {
                            console.warn(`❌ Advanced constraints attempt ${i + 1} failed:`, constraintError);
                            if (i === advancedAttempts.length - 1) {
                                console.error('🚨 All advanced constraint attempts failed!');
                            }
                        }
                    }

                    // Análisis de constraints avanzadas
                    const updatedSettings = audioTrack.getSettings();
                    await this.performAdvancedConstraintsAnalysis(audioTrack, updatedSettings);

                    // Si las constraints fallaron, mostrar advertencia específica
                    if (!constraintsSuccess) {
                        console.error('🚨 ADVERTENCIA CRÍTICA PARA ELECTRON:');
                        console.error('   - No se pudieron aplicar constraints avanzadas');
                        console.error('   - El filtro highpass puede seguir activo en Chromium embebido');
                        console.error('   - Esto explica por qué el cliente no escucha respiración');
                        console.error('   ');
                        if (runningInElectron) {
                            console.error('🖥️ SOLUCIONES ESPECÍFICAS PARA ELECTRON:');
                            console.error('   1. Revisar configuración de Electron en main.js:');
                            console.error('      - webPreferences: { nodeIntegration: true }');
                            console.error('      - Añadir flags: --disable-audio-processing');
                            console.error('   2. En el proceso principal de Electron:');
                            console.error('      - app.commandLine.appendSwitch("disable-audio-processing")');
                            console.error('      - app.commandLine.appendSwitch("disable-audio-input-processing")');
                            console.error('   3. Verificar versión de Electron/Chromium');
                            console.error('   4. Considerar downgrade a versión anterior de Electron');
                        } else {
                            console.error('🌐 Posible solución: Usar otro navegador o dispositivo');
                        }
                    }
                } else if (preserveLowFrequencies && audioTrack.applyConstraints) {
                    // Aplicar constraints sin análisis detallado
                    try {
                        await audioTrack.applyConstraints({
                            advanced: [{
                                googHighpassFilter: false,
                                googLowpassFilter: false,
                                googEchoCancellation: false,
                                googNoiseSuppression: false,
                                googAutoGainControl: false,
                            } as any]
                        });

                        // Solo análisis básico si el debug está deshabilitado
                        if (MEDICAL_DEVICE_DEBUG.enableSpectralAnalysis) {
                            await this.analyzeAudioStreamFilters(audioTrack);
                        }
                    } catch (constraintError) {
                        console.warn('Could not apply advanced constraints:', constraintError);
                    }
                }
            }

            return localStream;
        } catch (error) {
            console.error('Error getting medical device stream:', error);
            toast.error('Error al configurar dispositivo médico. Revise los permisos y la configuración.');
            return undefined;
        }
    }


    async getFilterStream(options: MediaStreamConstraints) {

        try {

          const defaultConstraints: MediaStreamConstraints = {
            video: {
              frameRate: { min: 5, ideal: 10, max: 15 },
              width: { min: 640, ideal: 1280, max: 1920 },
              height: { min: 480, ideal: 720, max: 1080 },
            }
          }

          // If the options contain a video device, we need to add the video constraints
          if (
           options.video && typeof options.video === 'object' &&
           defaultConstraints.video && typeof defaultConstraints.video === 'object'
          ) {
            options.video = {
              ...defaultConstraints.video,
              ...options.video,
            }
          }

          if (options.audio && typeof options.audio === 'object') {
            options.audio = {
              ...options.audio,
              noiseSuppression: false,
              echoCancellation: false
            }
          } else if (options.audio === true) {
            options.audio = {
              noiseSuppression: false,
              echoCancellation: false
            }
          }

          const localStream = await navigator.mediaDevices.getUserMedia(options);
          return localStream;

        }
        catch (e) {

            console.error('ERROR -> getDefaultDevice', e);

        }

    }


    /**
     * Retrieves the default media device (audio and video) stream.
     *
     * This function attempts to get the user's media devices with specific constraints
     * for audio and video. If a filter is provided, it checks if any of the audio or video
     * device labels match the filter. If a match is found, it logs an error and shows a warning
     * message indicating that the device is not valid.
     *
     * @param {string[]} [filter] - An optional array of device labels to filter out. If any of the
     * device labels match the filter, the function will return `undefined` and show a warning message.
     *
     * @returns {Promise<MediaStream | undefined>} - A promise that resolves to the media stream if successful,
     * or `undefined` if an error occurs or a filtered device is found.
     */
    async getDefaultDevice(filter?: string[]): Promise<MediaStream | undefined> {
      try {
        const localStream = await navigator.mediaDevices.getUserMedia({
            audio: {
                echoCancellation: false, // Deshabilitado para evitar filtrado de frecuencias bajas
                noiseSuppression: false, // Deshabilitado para preservar señal limpia del estetoscopio
                autoGainControl: false,  // Deshabilitado para mantener ganancia original
                frameRate: 10,
                sampleRate: 48000,       // 48kHz para mejor calidad médica
                suppressLocalAudioPlayback: true,
                googHighpassFilter: false, // Preservar frecuencias bajas críticas
            } as ExtendedAudioTrackConstraints,
            video: {
                frameRate: { min: 5, ideal: 10, max: 15 },
                echoCancellation: true,
                noiseSuppression: true,
                sampleRate: 44100,
                width: { min: 320, ideal: 640, max: 1280 },
                height: { min: 240, ideal: 480, max: 720 },
                zoom: true,
                pan: true,
                tilt: true,
            } as ExtendedVideoTrackConstraints
        });

        const audioTracks = localStream.getAudioTracks();
        const videoTracks = localStream.getVideoTracks();

        // Comprobar si alguno de los labels de audio o video coincide con los filtros
        if (filter) {
          console.log('DEBUG -> getDefaultDevice', 'Filter', filter);

          const audioFilter = audioTracks.find((audioTrack) => filter.includes(audioTrack.label));
          const videoFilter = videoTracks.find((videoTrack) => filter.includes(videoTrack.label));

          // Si encontramos un filtro, es que se usa un dispositivo que no se debe usar
          // Por lo que se debe mostrar un mensaje de error para informar de la
          // incompatibilidad del dispositivo
          if (audioFilter) {
            console.error('ERROR -> getDefaultDevice', 'Audio device not available', audioFilter.label);
            toast.warn(i18n.t('warn.device.audio.not.valid'));
            return undefined;
          }

          if (videoFilter) {
            console.error('ERROR -> getDefaultDevice', 'Video device not available', videoFilter.label);
            toast.warn(i18n.t('warn.device.video.not.valid'));
            return undefined;
          }
        }

        return localStream;
      }
      catch (e) {
        console.error('ERROR -> getDefaultDevice', e);
        toast.info(i18n.t('info.device.not.available'));
      }

      return undefined;
    }

    async getDevices(filter?: DeviceFilterType) {

        const devices = await navigator.mediaDevices.enumerateDevices();
        const filteredDevices = devices.filter((device) => {

            if (filter) {

                if (filter.audio && device.kind === 'audioinput') {

                    return true;

                }
                if (filter.video && device.kind === 'videoinput') {

                    return true;

                }

            }
            return false;

        });
        return filteredDevices;

    }

    async getDeviceMedia(devices: { videoId: string } | { audioId: string } | { videoId: string, audioId: string }, options?: ConnectDeviceOptions) {
        // First stop any existing background processing to prevent resource leaks
        if (options?.background) {
            await this.stopBackground();
        }

        let deviceAudio: MediaDeviceInfo | undefined = undefined
        let deviceVideo: MediaDeviceInfo | undefined = undefined
        if ('videoId' in devices) {

            // find device of video or audio
            const videoDevices = await this.getDevices({ video: true, });
            deviceVideo = videoDevices.find((device) => device.deviceId === devices.videoId);
            if (!deviceVideo) {

                throw new Error('Device not found');

            }

        }
        if ('audioId' in devices) {

            // find device of video or audio
            const audioDevices = await this.getDevices({ audio: true });
            deviceAudio = audioDevices.find((device) => device.deviceId === devices.audioId);
            if (!deviceAudio) {

                throw new Error('Device not found');

            }

        }

        // if is audio and video
        if ('audioId' in devices && 'videoId' in devices) {

            const localStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    deviceId: devices.audioId,
                    noiseSuppression: options?.noise ?? false, // Por defecto false para dispositivos médicos
                    echoCancellation: options?.echo ?? false,  // Por defecto false para dispositivos médicos
                    autoGainControl: false,                    // Deshabilitado para mantener señal original
                    frameRate: 10,
                    sampleRate: 48000,                        // 48kHz para mejor calidad médica
                    googHighpassFilter: false,                // Preservar frecuencias bajas críticas
                    googEchoCancellation: false,
                    googNoiseSuppression: false,
                    googAutoGainControl: false,
                } as ExtendedAudioTrackConstraints,
                video: {
                    deviceId: devices.videoId ? { exact: devices.videoId } : undefined,
                    frameRate: { min: 5, ideal: 10, max: 15 },
                    noiseSuppression: options?.noise,
                    echoCancellation: options?.echo,
                    width: { min: 640, ideal: 1280, max: 1920 },
                    height: { min: 480, ideal: 720, max: 1080 },
                    pan: true,
                    tilt: true,
                    zoom: true
                } as ExtendedVideoTrackConstraints,
            });

            console.log('localStream', localStream.getAudioTracks()[0]);
            console.log('localStream', localStream.getVideoTracks()[0]);

            if (options?.background) {
                try {
                    const streamBackground = await this.replaceBackground(localStream, options.background);
                    return streamBackground;
                } catch (error) {
                    console.error("Error applying background:", error);
                    return localStream;
                }
            }
            else return localStream;

        }
        // if is only audio
        else if (deviceAudio && 'audioId' in devices) {

            const localStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    deviceId: devices.audioId,
                    noiseSuppression: options?.noise ?? false, // Por defecto false para dispositivos médicos
                    echoCancellation: options?.echo ?? false,  // Por defecto false para dispositivos médicos
                    autoGainControl: false,                    // Deshabilitado para mantener señal original
                    sampleRate: 48000,                        // 48kHz para mejor calidad médica
                    googHighpassFilter: false,                // Preservar frecuencias bajas críticas
                    googEchoCancellation: false,
                    googNoiseSuppression: false,
                    googAutoGainControl: false,
                } as ExtendedAudioTrackConstraints,
                video: false,
            });

            console.log('localStream', localStream.getAudioTracks()[0]);
            console.log('localStream', localStream.getVideoTracks()[0]);

            return localStream;

        }
        // if is only video
        if (deviceVideo && deviceVideo.kind === 'videoinput' && 'videoId' in devices && deviceAudio === undefined) {

            try {

                const localStream = await navigator.mediaDevices.getUserMedia({
                    audio: false,
                    video: {
                        deviceId: devices.videoId,
                        frameRate: { min: 5, ideal: 10, max: 15 },
                        echoCancellation: options?.echo,
                        noiseSuppression: options?.noise,
                        // min: SD, ideal: HD, max: Full HD
                        width: { min: 640, ideal: 1280, max: 1920 },
                        height: { min: 480, ideal: 720, max: 1080 },
                        pan: true,
                        tilt: true,
                        zoom: true
                    } as ExtendedVideoTrackConstraints,
                });

                if (options?.background) {
                    try {
                        return await this.replaceBackground(localStream, options.background);
                    } catch (error) {
                        console.error("Error applying background:", error);
                        return localStream;
                    }
                }
                else return localStream;

            }
            catch (e) {

                console.error("Error getting video device:", e);
                throw e;

            }

        }

    }

    async getScreenMedias() {

        const stream = await navigator.mediaDevices.getDisplayMedia({
            video: {
                frameRate: { ideal: 10, max: 24 },
                width: { ideal: 1280, max: 1920 },
                height: { ideal: 720, max: 1080 },
            },
            audio: true,
        });

        return stream;

    }

    /**
     * Asynchronously requests permissions for audio and video media devices.
     *
     * @returns {Promise<{ audio: boolean, video: boolean, media: boolean }>}
     * A promise that resolves to an object indicating the permission status for audio, video, and media.
     *
     * @example
     * const permissions = await DeviceInstance.getPermissions();
     * console.log(permissions); // { audio: false, video: false, media: false }
     */
    static async getPermissions(): Promise<{ audio: boolean, video: boolean, media: boolean }> {

      const permissions = {
          audio: false,
          video: false,
          media: false,
      };

      // @ts-ignore
      await navigator.getUserMedia(
        {
          video: {
            pan: true,
            tilt: true,
            zoom: true,
            width: { min: 640, ideal: 1280, max: 1920 },
            height: { min: 480, ideal: 720, max: 1080 },
          },
        },
        function (stream: MediaStream) {
          stream.getTracks().forEach((x: any) => x.stop());
        },
        (err: any) => console.log(err)
      );

      // @ts-ignore
      await navigator.getUserMedia({ audio: true }, function (stream) {

        stream.getTracks().forEach((x: any) => x.stop());

      }, (err: any) => console.log(err));

      return permissions;

    }

    log(message: any, data?: any) {

    }

    async getAudioOutput() {

        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioOutput = devices.filter((device) => device.kind === 'audiooutput');
        return audioOutput;

    }

    async getAudioInput() {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInput = devices.filter((device) => device.kind === 'audioinput');
      return audioInput;
    }

    async getVideoInput() {

        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoInput = devices.filter((device) => device.kind === 'videoinput');
        return videoInput;

    }
}
